import { useActiveOrganization } from "@/context/ActiveOrganizationProvider";
import { usePromptEditorContext } from "@/context/PromptEditorContext";
import { useOrgToolConfigs } from "@/services/v4.service";
import { ContentState, EditorState } from "draft-js";
import { ForwardedRef, forwardRef, useCallback, useEffect, useMemo, useState } from "react";
import { useDebouncedCallback } from "use-debounce";
import { InputSuggestion } from "../AutocompletePanel";
import PromptEditorEditor from "../PromptEditorEditor";
import { createEditorState } from "../PromptEditorDecorators";
import { extractInputsFromEditorPromptState } from "../utils";

interface Props {
  initialContent: string;
  onContentChange?: (content: string) => void;
  onRun?: () => void;
  handleSave?: () => void;
  loading?: boolean;
  placeholder?: string;
  additionalVariables?: InputSuggestion[];
  hasDynamicVariables?: boolean;
  classNameOverrides?: string;
}

// TODO: This is 90% the same as PromptTemplate.tsx.  Can we combine them?
const MessageEditor = (
  {
    initialContent,
    onContentChange,
    onRun,
    handleSave,
    loading,
    placeholder,
    hasDynamicVariables,
    classNameOverrides,
    additionalVariables,
  }: Props,
  ref: ForwardedRef<HTMLDivElement>,
) => {
  const { editorPromptState, setVersionViewMode, versionViewMode } = usePromptEditorContext();
  const allowTools = hasDynamicVariables;

  const [editorState, setEditorState] = useState<EditorState>(() =>
    createEditorState(initialContent, hasDynamicVariables || false),
  );

  const onChange = useCallback(
    async (editorState: EditorState) => {
      const content = editorState.getCurrentContent();
      const contentString = content.getPlainText();

      // FYI, there was (and maybe still is) a bug where typing a variable closed
      // would sometimes produce a duplicate like '{{a}{{a}}'. It happened less when there
      // was a console.log here (so it might have been a timing issue).  Awaiting the change of
      // the message content before setting the editor state seems to have fixed it.
      onContentChange && (await onContentChange(contentString));
      setEditorState(editorState);
    },
    [onContentChange],
  );

  // Update editor state when initial content changes.
  // But only if the editor state's text is not the same as the initial content.
  // This is to avoid the editor state being updated when the user is typing.
  // And debounced+delayed or this will happen on every keystroke.
  // Note that the above was true when we had the below logic in the useEffect below,
  // leading to the useEffect requiring `editorState` as a dependency.
  // But now the useEffect is removed; the debounce may not be necessary. But reasonable to have anyway.
  const debouncedUpdateEditorState = useDebouncedCallback(() => {
    const content = editorState.getCurrentContent();
    const contentString = content.getPlainText();
    if (contentString !== initialContent) {
      // Note that if we do a normal `setEditorState(createEditorState(initialContent, hasDynamicVariables || false))`,
      // this doesn't seem to be sufficient for some reason.
      // The editor state is updated, and console logging `editorState.getCurrentContent().getPlainText()`
      // shows the correct text.  But the editor visually still shows the old text. (and editing lines is buggy)
      // So we use `EditorState.push` which seems to force the editor to update.
      console.log("Updating editor state due to initial content change", { contentString, initialContent });
      const newEditorState = EditorState.push(
        editorState,
        ContentState.createFromText(initialContent),
        "insert-characters",
      );
      setEditorState(newEditorState);
    }
  }, 100);
  useEffect(() => {
    debouncedUpdateEditorState();
    // Additional dependencies needed to trigger `debouncedUpdateEditorState` when needed.
    // (E.g. when initialContent changes)
  }, [initialContent, debouncedUpdateEditorState]);

  const { organization } = useActiveOrganization();
  const { tools } = useOrgToolConfigs(allowTools ? organization?.id : undefined, true);
  const [availableVariables, setAvailableVariables] = useState<InputSuggestion[]>([]);
  const inputVariables = useMemo(() => [...extractInputsFromEditorPromptState(editorPromptState)], [editorPromptState]);
  useEffect(() => {
    setAvailableVariables([
      ...inputVariables.map(
        (v) =>
          ({
            name: v,
            type: "input",
          }) as InputSuggestion,
      ),
      ...(additionalVariables || []),
    ]);
  }, [additionalVariables, inputVariables]);

  return (
    <div className={"w-full text-14-24"}>
      <PromptEditorEditor
        ref={ref}
        className={classNameOverrides ? classNameOverrides : ""}
        placeholder={loading ? "" : placeholder}
        handleSave={handleSave || NO_OP_HANDLER}
        handleSubmit={onRun || NO_OP_HANDLER}
        // cancelSubmit={NO_OP_HANDLER}
        additionalKeyBindingFn={(event) => {
          // mod + shift + e
          if (event.metaKey && event.shiftKey && (event.key === "E" || event.key === "e")) {
            setVersionViewMode(versionViewMode === "raw" ? "ui" : "raw");
            return "handled";
          }
        }}
        editorState={editorState}
        preview={false}
        onChange={onChange}
        readOnly={onContentChange === undefined}
        autocomplete={hasDynamicVariables}
        availableTools={tools}
        availableVariables={availableVariables}
      />
    </div>
  );
};

export default forwardRef(MessageEditor);

export const NO_OP_HANDLER = () => {};
